export type PositionChartMode = 'assets' | 'assetsUsd'

/** Decimals that scale the headline amount, or `null` when the asset's decimals are unknown. */
export function getHeadlineDecimals(
  mode: PositionChartMode,
  assetDecimals: number | null | undefined
): number | null {
  return mode === 'assetsUsd' ? 18 : (assetDecimals ?? null)
}

interface PositionChartStateInput {
  /** Points in the selected range, placeholder data included. */
  rangePointCount: number
  /** Points in the all-time history, once it was read. */
  allTimePointCount: number | undefined
  hasSnapshotBalance: boolean
}

/** The mode controls follow what the card can show: history in any range or a balance. */
export function canSwitchPositionMode({
  rangePointCount,
  allTimePointCount,
  hasSnapshotBalance
}: PositionChartStateInput): boolean {
  return rangePointCount > 0 || (allTimePointCount ?? 0) > 0 || hasSnapshotBalance
}

export interface PositionEmptyStateInput extends PositionChartStateInput {
  isPositionHistoryEnabled: boolean
  loading: boolean
  chartError: boolean
  isNotFound: boolean
  /** The selected range answered with no points (not placeholder data). */
  isRangeEmpty: boolean
  isAllTimeRange: boolean
  snapshotLoaded: boolean
  hasData: boolean
}

/**
 * Core answers a wallet that never held the vault with no points, and an unknown vault or wallet
 * with NOT_FOUND. An empty range is "no position" only when the all-time history is empty too
 * and the snapshot holds no balance: a wallet that exited before the range, or a balance Core
 * has not sampled yet, keeps the card. Waiting for an answer or a failed read keeps the card.
 */
export function showPositionEmptyState(input: PositionEmptyStateInput): boolean {
  const hasNoHistory =
    input.isRangeEmpty &&
    input.snapshotLoaded &&
    (input.isAllTimeRange || input.allTimePointCount === 0)
  return (
    input.isPositionHistoryEnabled &&
    !input.loading &&
    !input.chartError &&
    (input.isNotFound || hasNoHistory) &&
    !input.hasSnapshotBalance &&
    !input.hasData
  )
}

/**
 * History points that the selected mode cannot value (unknown decimals, no USD prices) are a
 * failed read, not a zero balance.
 */
export function isPositionSeriesUnavailable(
  pointCount: number,
  valuedPoints: readonly [number, number][]
): boolean {
  return pointCount > 0 && valuedPoints.length === 0
}

interface HeadlineUnavailableInput {
  mode: PositionChartMode
  isConnected: boolean
  /** The snapshot read failed without an answer to show; a failed refetch keeps the cached one. */
  snapshotFailed: boolean
  /** The `vaultV2.userPosition` snapshot position: `null` without shares, `undefined` while loading. */
  position: { assets: string | null; assetsUsd: number | null } | null | undefined
}

/**
 * A failed snapshot read, or a position the selected mode cannot value, shows the headline as
 * unavailable rather than a zero balance.
 */
export function isHeadlineUnavailable({
  mode,
  isConnected,
  snapshotFailed,
  position
}: HeadlineUnavailableInput): boolean {
  if (!isConnected) return false
  if (snapshotFailed) return true
  if (!position) return false
  return (mode === 'assetsUsd' ? position.assetsUsd : position.assets) === null
}
